import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { ArrowRight, ShieldCheck, Target, BarChart3, Clock, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/site/Layout";
import { ArticleCard } from "@/components/site/ArticleCard";
import { MarketPulse } from "@/components/site/MarketPulse";
import { StickyMobileCTA } from "@/components/site/StickyMobileCTA";
import { useAuth } from "@/lib/auth-context";

import { getFeaturedHomeArticles } from "@/lib/articles";
import record from "@/data/track-record.json";
import { MonthlyPerformanceTrajectory } from "@/components/site/MonthlyPerformanceTrajectory";
import heroBg from "@/assets/hero-bg.webp";

// Below-the-fold sections: lazy-loaded so they don't ship in the initial JS bundle
const PlatformFeatures = lazy(() =>
  import("@/components/site/PlatformFeatures").then((m) => ({ default: m.PlatformFeatures })),
);
const OrionHowItWorks = lazy(() =>
  import("@/components/site/OrionHowItWorks").then((m) => ({ default: m.OrionHowItWorks })),
);
const MethodValidation = lazy(() =>
  import("@/components/site/MethodValidation").then((m) => ({ default: m.MethodValidation })),
);
const AuditContextStrip = lazy(() =>
  import("@/components/site/AuditContextStrip").then((m) => ({ default: m.AuditContextStrip })),
);
const AISection = lazy(() =>
  import("@/components/site/AISection").then((m) => ({ default: m.AISection })),
);
const EconomicCalendar = lazy(() =>
  import("@/components/site/EconomicCalendar").then((m) => ({ default: m.EconomicCalendar })),
);
const FreeAccountSection = lazy(() =>
  import("@/components/site/FreeAccountSection").then((m) => ({ default: m.FreeAccountSection })),
);
const PropFirmSection = lazy(() =>
  import("@/components/site/PropFirmSection").then((m) => ({ default: m.PropFirmSection })),
);

// Sized skeleton so lazy chunks swap in without shifting layout (keeps CLS at 0)
const SectionFallback = ({ minHeight = 400 }: { minHeight?: number }) => (
  <div style={{ minHeight }} aria-hidden />
);


export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "XAU Pro Academy: Audited XAUUSD Decisions & SMC Analysis" },
      {
        name: "description",
        content:
          "Governed XAUUSD decisions by Orion, publicly validated and audited. SMC and ICT gold analysis for serious and prop-firm traders.",
      },
      {
        property: "og:title",
        content: "XAU Pro Academy | Institutional Gold Intelligence",
      },
      {
        property: "og:description",
        content:
          "Systematic Smart Money Concepts engineered for prop-firm desks and disciplined traders. Precision order execution with uncompromising risk parameters.",
      },
      { name: "twitter:title", content: "XAU Pro Academy | Institutional Gold Intelligence" },
      {
        name: "twitter:description",
        content:
          "Systematic Smart Money Concepts engineered for prop-firm desks and disciplined traders. Precision order execution with uncompromising risk parameters.",
      },
      { property: "og:image:alt", content: "XAU Pro Academy institutional gold intelligence terminal" },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://xauproacademy.com/" },
      { property: "og:image", content: "https://xauproacademy.com/og-image-v3.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://xauproacademy.com/og-image-v3.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: "https://xauproacademy.com/" },
      // LCP image preload, hashed asset URL so the browser starts the fetch
      // before the CSS parser resolves it via the <img> tag.
      { rel: "preload", as: "image", href: heroBg, fetchPriority: "high" },
    ],

    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "XAU Pro Academy",
          url: "https://xauproacademy.com",
          logo: "https://xauproacademy.com/logo.png",
          description:
            "XAU Pro Academy publishes governed XAUUSD trading decisions produced by the Orion engine, validated publicly and audited per decision. SMC and ICT gold analysis for serious and prop-firm gold traders.",
          areaServed: "Worldwide",
          sameAs: ["https://x.com/XAUProAcademy", "https://www.youtube.com/@XAUProAcademy"],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "EducationalOrganization",
          name: "XAU Pro Academy",
          url: "https://xauproacademy.com",
          logo: "https://xauproacademy.com/logo.png",
          description:
            "AI-assisted XAUUSD analysis and Smart Money Concepts education for serious gold traders and prop firm traders.",
          category: "Investment Advice",
          areaServed: "Worldwide",
          priceRange: "$$",
          sameAs: ["https://x.com/XAUProAcademy", "https://www.youtube.com/@XAUProAcademy"],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Published XAUUSD Decisions",
          serviceType: "Gold (XAUUSD) trading decision publication and validation",
          provider: {
            "@type": "Organization",
            name: "XAU Pro Academy",
            url: "https://xauproacademy.com",
          },
          areaServed: "Worldwide",
          audience: {
            "@type": "Audience",
            audienceType: "XAUUSD traders, SMC traders, ICT traders, prop firm traders",
          },
          description:
            "Live XAUUSD trading decisions produced by the Orion decision engine using Smart Money Concepts, with a public validation hub and per-decision audit context. An institutional alternative to traditional gold signal services.",
          url: "https://xauproacademy.com/signals",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "XAU Pro Academy",
          url: "https://xauproacademy.com",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://xauproacademy.com/education?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
    ],
  }),
  component: Index,
});

// Verified performance strip, taken directly from the audited 2024 to 2026
// record (2024 +20.50R, 2025 +49.50R, 2026 +24.25R). Keep in sync with
// /track-record.
const evidenceStrip = [
  { label: "Verified Trades", value: "241", note: "2024 to 2026 record" },
  { label: "Net Result", value: "+95.94R", note: "Audited outcome" },
  { label: "Profit Factor", value: "1.72", note: "Gross wins / gross losses" },
  { label: "Max Drawdown", value: "8.25R", note: "Peak to trough" },
];

const features = [
  { icon: Target, title: "SMC Methodology", desc: "Order blocks, liquidity sweeps and FVGs, institutional logic explained step-by-step." },
  { icon: ShieldCheck, title: "Risk-First Execution", desc: "Every case study is annotated with SL & dual TPs. We teach max 1% account risk per trade." },
  { icon: BarChart3, title: "Transparent Trade Log", desc: "Live internal log: wins and losses, all on the record. Learn from real outcomes." },
  { icon: Clock, title: "London & NY Sessions", desc: "Analysis during high-liquidity windows. We focus on the sessions that matter." },
];

function Index() {
  const featuredArticles = getFeaturedHomeArticles();
  const { user, isHydrated } = useAuth();
  const member = isHydrated && user;

  return (
    <Layout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-hero">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <img
          src={heroBg}
          alt="XAU Pro Academy institutional gold trading desk with live XAUUSD market analysis"
          width={1920}
          height={1080}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover opacity-25 mix-blend-luminosity"
        />

        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/40 to-background" />

        <div className="container relative mx-auto max-w-7xl px-4 pb-24 pt-20 md:pt-32">
          <div className="mx-auto max-w-3xl text-center">
            <MarketPulse />

            <div className="mt-12 flex justify-center md:mt-14">
              <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold-muted px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-gold">
                Governed XAUUSD Decisions
              </span>
            </div>
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Published. Validated. Audited. AI-assisted.
            </p>


            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.05] tracking-tight md:text-6xl lg:text-7xl">
              Published <span className="text-gradient-gold">XAUUSD Decisions</span>. Not a Signal Group.
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base text-muted-foreground md:text-lg">
              What traders search for as gold signals, we publish as governed XAUUSD decisions.
              Each decision is produced by the Orion engine, logged with full reasoning, and
              measured against the verified 2024 to 2026 record. Built for serious gold traders.
            </p>


            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              {member ? (
                <Button asChild variant="gold" size="xl">
                  <Link to="/dashboard">
                    Open XAU Terminal™
                  </Link>
                </Button>
              ) : (
                <Button asChild variant="gold" size="xl">
                  <Link to="/auth" search={{ tab: "signup" }}>
                    Start Free <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              )}
              <Button asChild variant="outline" size="xl">
                <Link to="/signals">See Live XAUUSD Decisions</Link>
              </Button>
            </div>
            {!member && (
              <p className="mt-3 text-xs text-muted-foreground">
                Free accounts include AI chart analysis, market alerts, and the public trade log.
                You trade on your own broker. We do not execute trades.
              </p>
            )}
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              <Link to="/signals" className="transition-colors hover:text-gold">Live XAUUSD signals</Link>
              <span className="text-border">·</span>
              <Link to="/gold-analysis" className="transition-colors hover:text-gold">Gold market analysis</Link>
              <span className="text-border">·</span>
              <Link to="/smart-money-concepts" className="transition-colors hover:text-gold">SMC &amp; ICT gold</Link>
              <span className="text-border">·</span>
              <Link to="/prop-firm-gold" className="transition-colors hover:text-gold">Prop-firm discipline</Link>
              <span className="text-border">·</span>
              <Link to="/orion/how-it-works" className="transition-colors hover:text-gold">AI gold analysis</Link>
            </div>

            <p className="mt-4 text-[11px] uppercase tracking-wider text-muted-foreground">
              For educational purposes only. Not financial advice. Not a broker or prop firm.
            </p>
          </div>

          {/* Verified performance strip, from the audited 2024 to 2026 record */}
          <div className="mx-auto mt-16 max-w-4xl">
            <div className="mb-2 text-center font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
              Verified Performance Record
            </div>
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-border/60 bg-border/60 md:grid-cols-4">
              {evidenceStrip.map((s) => (
                <div key={s.label} className="bg-card/80 px-5 py-6 text-center backdrop-blur">
                  <div className="font-display text-2xl font-bold text-gradient-gold md:text-3xl">{s.value}</div>
                  <div className="mt-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{s.label}</div>
                  <div className="mt-1 text-[10px] text-muted-foreground/70">{s.note}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 text-center">
              <Link
                to="/track-record"
                className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-gold"
              >
                View the full Performance Record &rarr;
              </Link>
            </div>
          </div>


        </div>
      </section>

      {/* Platform Features */}
      <Suspense fallback={<SectionFallback minHeight={500} />}>
        <PlatformFeatures />
      </Suspense>


      {/* How it works, powered by Orion */}
      <Suspense fallback={<SectionFallback minHeight={600} />}>
        <OrionHowItWorks />
      </Suspense>

      {/* Method and Validation */}
      <Suspense fallback={<SectionFallback minHeight={500} />}>
        <MethodValidation />
      </Suspense>

      {/* Audit context binding: every published decision sits inside this window */}
      <Suspense fallback={<SectionFallback minHeight={260} />}>
        <AuditContextStrip />
      </Suspense>



      {/* Verified three-year performance */}
      <section className="border-y border-border/60 bg-slate-deep">
        <div className="container mx-auto max-w-7xl px-4 py-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
              <span className="h-px w-4 bg-gold" /> Three-Year Evidence
            </div>
            <h2 className="mt-3 font-display text-3xl font-bold md:text-4xl">
              Verified XAUUSD Execution Record
            </h2>
            <p className="mt-3 text-muted-foreground">
              Continuous monthly performance from January 2024 through October 2026. Every rise
              and drawdown remains visible across the complete 241-position record.
            </p>
          </div>

          <div className="mt-9 grid gap-px overflow-hidden rounded-md border border-border/60 bg-border/60 md:grid-cols-3">
            {record.years.map((year) => (
              <article key={year.year} className="relative bg-background/80 p-5 md:p-6">
                {year.year === 2026 ? <div className="absolute inset-x-0 top-0 h-px bg-gold" /> : null}
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    {year.year === 2026 ? "2026 · To October" : year.year}
                  </span>
                  <span className="font-mono text-[9px] uppercase tracking-wider text-gold">Audited</span>
                </div>
                <div className="mt-3 font-mono text-3xl font-semibold tabular-nums text-emerald">
                  +{year.overall.netR.toFixed(2)}R
                </div>
                <dl className="mt-5 grid grid-cols-3 gap-3 border-t border-border/50 pt-4">
                  <div>
                    <dt className="font-mono text-[8px] uppercase tracking-wider text-muted-foreground">Positions</dt>
                    <dd className="mt-1 font-mono text-sm tabular-nums text-foreground">{year.overall.trades}</dd>
                  </div>
                  <div>
                    <dt className="font-mono text-[8px] uppercase tracking-wider text-muted-foreground">Win rate</dt>
                    <dd className="mt-1 font-mono text-sm tabular-nums text-foreground">{year.overall.winRate}%</dd>
                  </div>
                  <div>
                    <dt className="font-mono text-[8px] uppercase tracking-wider text-muted-foreground">Profit factor</dt>
                    <dd className="mt-1 font-mono text-sm tabular-nums text-foreground">{year.overall.profitFactor.toFixed(2)}</dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>

          <div className="mt-5">
            <MonthlyPerformanceTrajectory />
          </div>

          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button asChild variant="gold">
              <Link to="/track-record">
                Full Performance Record <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="ghost">
              <Link to="/case-studies">Live Desk Case Studies</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why XAU Pro Academy */}
      <section className="border-y border-border/60 bg-slate-deep">
        <div className="container mx-auto max-w-7xl px-4 py-20">
          <div className="mx-auto max-w-2xl text-center">
            <div className="text-xs font-semibold uppercase tracking-wider text-gold">Why XAU Pro Academy</div>
            <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">Built for Discipline, Not Hype</h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div key={f.title} className="rounded-xl border border-border/60 bg-card p-6 transition hover:border-gold/40">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-gold-muted text-gold">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Positioning */}
      <Suspense fallback={<SectionFallback minHeight={500} />}>
        <AISection />
      </Suspense>

      {/* Market Agenda */}
      <section className="border-y border-border/60 bg-background">
        <div className="container mx-auto max-w-7xl px-4 py-14 md:py-16">
          <div className="mx-auto max-w-2xl text-center">
            <div className="text-xs font-semibold uppercase tracking-wider text-gold">Market Agenda</div>
            <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">This Week's High-Impact Gold Catalysts</h2>
            <p className="mt-3 text-sm text-muted-foreground md:text-base">
              USD high-impact data, FOMC, NFP, CPI, GDP, is what moves XAU/USD.
              Track the calendar so you're never blindsided by a release.
            </p>
          </div>
          <Suspense fallback={<SectionFallback minHeight={400} />}>
            <EconomicCalendar />
          </Suspense>
        </div>
      </section>

      {/* Free Account */}
      <Suspense fallback={<SectionFallback minHeight={400} />}>
        <FreeAccountSection />
      </Suspense>

      {/* Education Hub */}
      <section className="container mx-auto max-w-7xl px-4 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gold">
            <GraduationCap className="h-4 w-4" /> XAU Pro Academy
          </div>
          <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">
            Learn the Logic Behind Every Trade
          </h2>
          <p className="mt-3 text-muted-foreground">
            Structured Smart Money Concepts lessons built around real XAUUSD execution:
            liquidity, market structure, institutional concepts, and execution psychology.
          </p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {featuredArticles.map((a, i) => (
            <ArticleCard key={a.slug} article={a} featured={i === 0} />
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <Button asChild variant="gold" size="lg">
            <Link to="/education">
              View All Lessons <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Prop Firm */}
      <Suspense fallback={<SectionFallback minHeight={400} />}>
        <PropFirmSection />
      </Suspense>

      {/* Sticky Mobile CTA */}

      <StickyMobileCTA />
    </Layout>
  );
}

